import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import { Button, Divider, Stack, TextField, CircularProgress, Modal, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Papa from "papaparse";
import { useSnackbar } from "notistack";
import { useHistory, useLocation } from "react-router-dom";
import { debounce } from "lodash";

import {
  deleteOrganizationMemberRoute,
  getAssignableOfficialCategoriesRoute,
  getOrganizationMembersRoute,
  mergeOrganizationMemberRoute,
  organizationMemberDeletionImpactRoute,
  organizationMembersExportRoute,
  organizationMembersImportRoute,
  organizationMembershipsRoute,
  updateOrganizationMemberRoute,
} from "network/api-routes";
import { membershipByPersonId } from "utils/membership-ui-helpers";
import performRequest from "network/perform-request";
import UserContext from "../../../../context/UserContext";
import RevaText from "components/common/RevaText";
import ImportSection from "./ImportSection";
import CsvPreview from "./CsvPreview";
import ImportResult from "./ImportResult";
import MembersTable from "./MembersTable";
import MembersTablePagination from "./MembersTablePagination";
import OrganizationMembershipPanel from "./OrganizationMembershipPanel";
import EditMemberPage from "./MemberEditPage";
import MemberDeletionDialog from "./MemberDeletionDialog";
import MemberMergeDialog from "./MemberMergeDialog";
import MemberStatusDialog from "./MemberStatusDialog";
import { useIntl } from "react-intl";

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

function MembersIndexPage(props) {
  const queryParams = useQueryParams();

  const initialQueryRef = useRef(queryParams.get("query") || "");
  const initialPageRef = useRef(parseInt(queryParams.get("page") || "1", 10));
  const initialStatusRef = useRef(
    queryParams.get("is_active") === "true" ? "active" : queryParams.get("is_active") === "false" ? "inactive" : "all",
  );
  const initialCategoryRef = useRef(
    queryParams.get("without_official_category") === "true"
      ? "none"
      : queryParams.get("official_category_id") || "all",
  );
  const isSearchTriggered = useRef(false);
  const loadedOrganizationId = useRef(null);

  const { user, selectedOrganization } = useContext(UserContext);
  const [membersResponse, setMembersResponse] = useState(null);
  const [membershipsByMemberId, setMembershipsByMemberId] = useState(new Map());
  const [panelMember, setPanelMember] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [query, setQuery] = useState(initialQueryRef.current);
  const [currentPage, setCurrentPage] = useState(initialPageRef.current);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [searching, setSearching] = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [isActiveFilter, setIsActiveFilter] = useState(initialStatusRef.current); // "all" | "active" | "inactive"
  const [officialCategories, setOfficialCategories] = useState([]);
  const [officialCategoryFilter, setOfficialCategoryFilter] = useState(initialCategoryRef.current);
  const [statusMember, setStatusMember] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [deletionMember, setDeletionMember] = useState(null);
  const [deletionImpact, setDeletionImpact] = useState(null);
  const [deletionImpactLoading, setDeletionImpactLoading] = useState(false);
  const [deletionError, setDeletionError] = useState("");
  const [deletionSubmitting, setDeletionSubmitting] = useState(false);
  const [mergeMember, setMergeMember] = useState(null);
  const [mergeImpact, setMergeImpact] = useState(null);
  const [mergeSubmitting, setMergeSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const intl = useIntl();

  const fetchPage = useCallback(
    (page = currentPage, search = query, status = isActiveFilter, category = officialCategoryFilter) => {
      if (!selectedOrganization) return;

      setSearching(true);
      const params = {
        organization_id: selectedOrganization.id,
        query: search,
      };

      if (status === "active") params.is_active = true;
      if (status === "inactive") params.is_active = false;
      if (category === "none") params.without_official_category = true;
      if (category !== "all" && category !== "none") params.official_category_id = category;

      performRequest("get", getOrganizationMembersRoute, params, true, user, page)
        .then((response) => {
          setMembersResponse(response.data);
          if (currentPage !== page) {
            setCurrentPage(page); // solo si no coincide
          }
        })
        .catch((error) => {
          setMembersResponse(null);
          enqueueSnackbar(error?.response?.data?.message || "Lo sentimos, hubo un error", { variant: "error" });
        })
        .finally(() => setSearching(false));
    },
    [currentPage, enqueueSnackbar, isActiveFilter, officialCategoryFilter, query, selectedOrganization, user],
  );

  const reloadMemberships = useCallback(() => {
    if (!selectedOrganization) return;
    performRequest("get", organizationMembershipsRoute(selectedOrganization.id), {}, true, user)
      .then((response) => setMembershipsByMemberId(membershipByPersonId(response.data?.data || [], "OrganizationMember")))
      .catch(() => setMembershipsByMemberId(new Map()));
  }, [selectedOrganization, user]);

  useEffect(() => {
    if (!selectedOrganization || loadedOrganizationId.current === selectedOrganization.id) return;

    loadedOrganizationId.current = selectedOrganization.id;
    fetchPage(currentPage, query, isActiveFilter, officialCategoryFilter);
    reloadMemberships();
    performRequest("GET", getAssignableOfficialCategoriesRoute(selectedOrganization.id), {}, true, user)
      .then((response) => setOfficialCategories(response.data.data || []))
      .catch(() => setOfficialCategories([]));
  }, [currentPage, fetchPage, isActiveFilter, officialCategoryFilter, query, reloadMemberships, selectedOrganization, user]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set("query", query);
    if (currentPage > 1) params.set("page", currentPage);
    if (isActiveFilter !== "all") params.set("is_active", isActiveFilter === "active" ? "true" : "false");
    if (officialCategoryFilter === "none") params.set("without_official_category", "true");
    if (officialCategoryFilter !== "all" && officialCategoryFilter !== "none") {
      params.set("official_category_id", officialCategoryFilter);
    }
    history.replace({ search: params.toString() });
  }, [query, currentPage, isActiveFilter, officialCategoryFilter, history]);

  const debouncedFetch = useMemo(
    () => debounce((page, value, status, category) => fetchPage(page, value, status, category), 500),
    [fetchPage],
  );

  useEffect(() => {
    if (isSearchTriggered.current) {
      debouncedFetch(1, query, isActiveFilter, officialCategoryFilter); // reset page = 1 solo si fue typing o cambio filtro
      isSearchTriggered.current = false;
    }
  }, [query, isActiveFilter, officialCategoryFilter, debouncedFetch]);

  useEffect(() => {
    return () => {
      debouncedFetch.cancel(); // cancela al desmontar
    };
  }, [debouncedFetch]);

  const handleCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Resetear el input para que dispare el onChange incluso con el mismo archivo
    e.target.value = null;

    setCsvFile(file);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false,
      complete: (results) => {
        setCsvPreview(results.data);
      },
    });
  };

  const handleImport = async () => {
    if (!csvFile || !selectedOrganization) return;

    const formData = new FormData();
    formData.append("file", csvFile);
    formData.append("organization_id", selectedOrganization.id);

    performRequest(
      "POST",
      organizationMembersImportRoute, // Replace with your actual import route
      formData,
      true,
      user,
    )
      .then((response) => {
        setCsvFile(null);
        setCsvPreview([]);
        setImportSummary({
          message: response.data.message,
          totalProcessed: response.data.total_processed,
          totalImported: response.data.total_imported,
          totalSkipped: response.data.total_skipped,
          skippedList: response.data.skipped || [],
        });
        fetchPage(); // Refresh member list
        enqueueSnackbar("Importación completada", {
          variant: "success",
        });
      })
      .catch((error) => {
        enqueueSnackbar(error?.response?.data?.message || "Hubo un error al importar", {
          variant: "error",
        });
      });
  };

  const handleExport = () => {
    const params = {
      organization_id: selectedOrganization.id,
      query,
    };

    if (isActiveFilter === "active") params.is_active = true;
    if (isActiveFilter === "inactive") params.is_active = false;

    performRequest("get", organizationMembersExportRoute, params, true, user)
      .then((res) => {
        const members = res.data.data || [];

        const csvData = members.map((m) => ({
          first_name: (m.name || "").split(" ").slice(0, 1).join(""),
          last_name: (m.name || "").split(" ").slice(1).join(""),
          cellphone_number: m.cellphone_number || "",
          document_number: m.document_number || "",
          member_number: m.member_number || "",
          birth_date: m.birth_date || "",
          category_code: m.category_code || "",
        }));

        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `miembros_${selectedOrganization.slug}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch(() => {
        enqueueSnackbar("Hubo un error al exportar los miembros", { variant: "error" });
      });
  };

  const handleOpenMembership = (member) => {
    setPanelMember(member);
  };

  const handleEditClick = (memberId) => {
    setSelectedMemberId(memberId);
    setIsEditModalOpen(true);
  };

  const fetchDeletionImpact = useCallback(
    async (member) => {
      const response = await performRequest(
        "GET",
        organizationMemberDeletionImpactRoute(selectedOrganization.id, member.id),
        {},
        true,
        user,
      );
      return response.data;
    },
    [selectedOrganization?.id, user],
  );

  const handleStatusClick = (member) => setStatusMember(member);

  const updateMemberStatus = async (member, isActive) => {
    await performRequest("PUT", updateOrganizationMemberRoute(member.id), { is_active: isActive }, true, user);
    enqueueSnackbar(
      intl.formatMessage({
        id: isActive
          ? "members.lifecycle.reactivatedSuccess"
          : "members.lifecycle.deactivatedSuccess",
      }),
      { variant: "success" },
    );
    fetchPage();
  };

  const handleConfirmStatus = async () => {
    if (!statusMember) return;

    setStatusSubmitting(true);
    try {
      await updateMemberStatus(statusMember, !statusMember.is_active);
      setStatusMember(null);
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || intl.formatMessage({ id: "members.lifecycle.statusUpdateError" }),
        { variant: "error" },
      );
    } finally {
      setStatusSubmitting(false);
    }
  };

  const handleDeleteClick = async (member) => {
    setDeletionMember(member);
    setDeletionImpact(null);
    setDeletionError("");
    setDeletionImpactLoading(true);

    try {
      setDeletionImpact(await fetchDeletionImpact(member));
    } catch (error) {
      setDeletionError(
        error?.response?.data?.message || intl.formatMessage({ id: "members.lifecycle.historyCheckError" }),
      );
    } finally {
      setDeletionImpactLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletionMember) return;

    setDeletionSubmitting(true);
    try {
      await performRequest("DELETE", deleteOrganizationMemberRoute(deletionMember.id), {}, true, user);
      enqueueSnackbar(intl.formatMessage({ id: "confirm.deleteMemberSuccess" }), { variant: "success" });
      setDeletionMember(null);
      fetchPage();
    } catch (error) {
      if (error?.response?.status === 409 && error.response.data?.code === "member_has_dependencies") {
        setDeletionImpact(error.response.data);
        setDeletionError("");
      } else {
        setDeletionError(
          error?.response?.data?.message || intl.formatMessage({ id: "confirm.deleteMemberErrorMessage" }),
        );
      }
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const handleDeactivateFromDeletion = async () => {
    if (!deletionMember) return;

    setDeletionSubmitting(true);
    try {
      await updateMemberStatus(deletionMember, false);
      setDeletionMember(null);
    } catch (error) {
      setDeletionError(
        error?.response?.data?.message || intl.formatMessage({ id: "members.lifecycle.deactivateError" }),
      );
    } finally {
      setDeletionSubmitting(false);
    }
  };

  const handleMergeClick = async (member) => {
    setMergeMember(member);
    setMergeImpact(null);

    try {
      setMergeImpact(await fetchDeletionImpact(member));
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || intl.formatMessage({ id: "members.lifecycle.historyLoadError" }),
        { variant: "error" },
      );
    }
  };

  const handleMergeFromDeletion = () => {
    setMergeMember(deletionMember);
    setMergeImpact(deletionImpact);
    setDeletionMember(null);
  };

  const handleConfirmMerge = async (target) => {
    if (!mergeMember || !target) return;

    setMergeSubmitting(true);
    try {
      const response = await performRequest(
        "POST",
        mergeOrganizationMemberRoute(selectedOrganization.id, mergeMember.id),
        { target_member_id: target.id },
        true,
        user,
      );
      enqueueSnackbar(
        response.data?.message || intl.formatMessage({ id: "members.lifecycle.mergeSuccess" }),
        { variant: "success" },
      );
      setMergeMember(null);
      setMergeImpact(null);
      fetchPage();
    } catch (error) {
      enqueueSnackbar(
          error?.response?.data?.errors?.target_member_id?.[0] ||
          error?.response?.data?.message ||
          intl.formatMessage({ id: "members.lifecycle.mergeError" }),
        { variant: "error" },
      );
    } finally {
      setMergeSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setIsEditModalOpen(false);
    setSelectedMemberId(null);
  };

  return (
    <Stack>
      <Stack sx={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
        <RevaText variant="h4">Miembros {selectedOrganization?.name}</RevaText>
        <Stack
          gap={1}
          sx={{ flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: "center" }}
        >
          <Button variant="outlined" onClick={() => setShowImport(!showImport)}>
            {showImport ? "Ocultar" : "Importar/Exportar"}
          </Button>
          <Button variant="contained" onClick={() => setIsEditModalOpen(true)}>
            Crear miembro
          </Button>
        </Stack>
      </Stack>
      <Divider flexItem sx={{ my: 2 }} />
      {showImport && (
        <ImportSection csvFile={csvFile} handleCsvFileChange={handleCsvFileChange} handleExportClick={handleExport} />
      )}

      {csvPreview.length > 0 && (
        <CsvPreview
          file={csvFile}
          preview={csvPreview}
          onCancel={() => {
            setCsvFile(null);
            setCsvPreview([]);
          }}
          onImport={handleImport}
        />
      )}

      {importSummary && <ImportResult summary={importSummary} />}

      <Stack
        gap={2}
        sx={{
          mb: 2,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <RevaText variant="body2" sx={{ order: { xs: 2, sm: 1 } }}>
          {membersResponse?.total ?? 0} resultados encontrados
        </RevaText>
        <Stack direction="row" sx={{ alignItems: "center", gap: 2, order: { xs: 1, sm: 2 } }}>
          {searching && <CircularProgress size={20} sx={{ mr: 2 }} />}
          <TextField
            select
            label="Filtrar por estado"
            value={isActiveFilter}
            size="small"
            onChange={(e) => {
              isSearchTriggered.current = true;
              setIsActiveFilter(e.target.value);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Activos</MenuItem>
            <MenuItem value="inactive">Inactivos</MenuItem>
          </TextField>
          <TextField select label={intl.formatMessage({ id: "Categoría oficial" })} value={officialCategoryFilter} size="small" onChange={(e) => {
            isSearchTriggered.current = true;
            setOfficialCategoryFilter(e.target.value);
          }} sx={{ minWidth: 190 }}>
            <MenuItem value="all">{intl.formatMessage({ id: "Todas" })}</MenuItem>
            <MenuItem value="none">{intl.formatMessage({ id: "Sin categoría" })}</MenuItem>
            {officialCategories.map((category) => <MenuItem key={category.id} value={category.id}>{category.ladder.name} · {category.name}</MenuItem>)}
          </TextField>
          <TextField
            placeholder="Buscar..."
            value={query}
            onChange={(e) => {
              isSearchTriggered.current = true;
              setQuery(e.target.value);
            }}
            size="small"
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Stack>
      </Stack>

      <Stack gap={2}>
        <MembersTable
          members={membersResponse?.data || []}
          selectedOrganization={selectedOrganization}
          onEditClick={handleEditClick}
          onOpenMembership={handleOpenMembership}
          onStatusClick={handleStatusClick}
          onMergeClick={handleMergeClick}
          onDeleteClick={handleDeleteClick}
        />

        {membersResponse && (
          <MembersTablePagination
            current={membersResponse.current_page}
            last={membersResponse.last_page}
            hasNext={!!membersResponse.next_page_url}
            hasPrev={!!membersResponse.prev_page_url}
            onChange={(page) => {
              setCurrentPage(page);
              fetchPage(page, query, isActiveFilter, officialCategoryFilter);
            }}
          />
        )}
      </Stack>

      <OrganizationMembershipPanel
        open={Boolean(panelMember)}
        onClose={() => setPanelMember(null)}
        organization={selectedOrganization}
        member={panelMember}
        membership={panelMember ? membershipsByMemberId.get(panelMember.id) : null}
        user={user}
        onUpdated={() => reloadMemberships()}
      />
      <MemberStatusDialog
        member={statusMember}
        open={Boolean(statusMember)}
        submitting={statusSubmitting}
        onClose={() => setStatusMember(null)}
        onConfirm={handleConfirmStatus}
      />
      <MemberDeletionDialog
        member={deletionMember}
        impact={deletionImpact}
        loading={deletionImpactLoading}
        error={deletionError}
        open={Boolean(deletionMember)}
        submitting={deletionSubmitting}
        onClose={() => setDeletionMember(null)}
        onDelete={handleConfirmDelete}
        onDeactivate={handleDeactivateFromDeletion}
        onMerge={handleMergeFromDeletion}
      />
      <MemberMergeDialog
        source={mergeMember}
        organizationId={selectedOrganization?.id}
        impact={mergeImpact}
        open={Boolean(mergeMember)}
        submitting={mergeSubmitting}
        onClose={() => {
          setMergeMember(null);
          setMergeImpact(null);
        }}
        onConfirm={handleConfirmMerge}
      />
      <Modal open={isEditModalOpen} onClose={handleCloseModal}>
        <EditMemberPage
          memberId={selectedMemberId}
          organization={selectedOrganization}
          onClose={handleCloseModal}
          onSuccess={() => fetchPage()} // asumí que esta es la función para refrescar
        />
      </Modal>
    </Stack>
  );
}

export default MembersIndexPage;
